import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Building2, Users, Landmark, Leaf, Award, Clock, MapPin, ClipboardCheck, PencilRuler, Sprout, Scissors, Construction, Droplets, TreePine, Flower2 } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import CallbackForm from "@/components/public/CallbackForm";
import GoogleReviews from "@/components/public/GoogleReviews";
import api, { fileUrl } from "@/lib/api";
import Seo, { localBusinessLd } from "@/components/Seo";

const ICONS = { ClipboardCheck, PencilRuler, Sprout, Scissors, Construction, Droplets, TreePine, Flower2 };
const HERO = fileUrl("acanthe-jardins/uploads/2de984f4-be59-49e8-af2d-a1ffb34c56f0.png");
const HERO_VIDEO = "acanthe-jardins/uploads/459b6954-cadc-42de-b8ee-76391b93a253.mp4";

const segments = [
  { icon: Landmark, title: "Collectivités", desc: "Parcs, espaces publics, terrains de sport, mobilier urbain et aménagements paysagers pour les communes." },
  { icon: Building2, title: "Entreprises", desc: "Espaces verts d'entreprise, sièges sociaux, zones d'activité et entretien de vos extérieurs professionnels." },
  { icon: Users, title: "Particuliers", desc: "Jardins sur-mesure, terrasses, piscines biologiques, oliveraies et entretien de votre propriété." },
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [realisations, setRealisations] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/realisations").then((r) => setRealisations(r.data.slice(0, 6)));
  }, []);

  return (
    <div>
      <Seo path="/" jsonLd={localBusinessLd()} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay muted loop playsInline preload="auto"
          poster={HERO}
          data-testid="hero-video"
        >
          <source src={fileUrl(HERO_VIDEO)} type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
          <div className="max-w-3xl animate-fade-up">
            <Overline className="text-terracotta">Paysagistes · Bouches-du-Rhône · Var · Vaucluse</Overline>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-cream text-balance">
              Vos experts en aménagement extérieur
            </h1>
            <p className="mt-7 text-lg text-cream/80 max-w-xl leading-relaxed">
              Du terrassement à la création d'espaces verts, Acanthe Jardins conçoit, réalise et entretient vos extérieurs pour les collectivités, entreprises et particuliers.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-semibold text-cream hover:bg-white hover:text-forest transition-colors">
                Demander une étude gratuite <ArrowRight size={18} />
              </Link>
              <Link to="/realisations" className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/40 px-8 py-4 font-semibold text-cream hover:bg-cream/10 transition-colors">
                Voir nos réalisations
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-forest py-4 border-y border-cream/10">
        <Marquee speed={40} gradient={false}>
          {["Étude & Diagnostics", "Conception", "Espaces verts", "Entretien", "Terrassement", "Bassins & Irrigation", "Pépinière", "Mobilier urbain"].map((t, i) => (
            <span key={i} className="mx-8 font-display text-2xl text-cream/50 flex items-center gap-8">{t} <Leaf size={16} className="text-terracotta" /></span>
          ))}
        </Marquee>
      </div>

      {/* INTRO / STATS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>Notre savoir-faire</Overline>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest leading-tight">
            Un métier, une passion : sublimer vos espaces extérieurs
          </h2>
          <p className="mt-6 text-stone leading-relaxed">
            Fondée il y a plus de 14 ans dans la région des Bouches-du-Rhône, l'entreprise Acanthe Jardins a élargi son activité aux départements du Var et du Vaucluse. Notre savoir-faire est axé sur l'aménagement extérieur, du terrassement à la création d'espaces verts, dans le respect de nos engagements qualité.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {[{ n: "14+", l: "Ans d'expérience" }, { n: "3", l: "Départements" }, { n: "100%", l: "Sur-mesure" }].map((s) => (
              <div key={s.l}>
                <p className="font-display text-4xl text-brand">{s.n}</p>
                <p className="text-xs uppercase tracking-wider text-stone mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="grid grid-cols-2 gap-4">
          {[Award, Clock, MapPin, Leaf].map((Icon, i) => (
            <div key={i} className={`rounded-2xl p-6 border border-border ${i % 2 ? "bg-sage" : "bg-white"} ${i === 0 ? "translate-y-6" : ""} ${i === 3 ? "translate-y-6" : ""}`}>
              <Icon className="text-brand mb-3" size={28} />
              <p className="font-semibold text-forest text-sm">
                {["Qualité garantie", "Réactivité", "Ancrage local", "Éco-responsable"][i]}
              </p>
              <p className="text-xs text-stone mt-1">
                {["Travaux soignés et durables", "Interventions rapides", "Sud de la France", "Respect de l'environnement"][i]}
              </p>
            </div>
          ))}
        </Reveal>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-background_alt bg-[#F0EFEA] py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <Overline>Nos prestations</Overline>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest">Des solutions complètes pour tous vos projets</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = ICONS[s.icon] || Leaf;
              return (
                <Reveal key={s.id} delay={(i % 4) * 0.08}>
                  <Link to={`/prestations/${s.slug}`} data-testid={`service-card-${s.slug}`}
                    className="group block h-full rounded-2xl bg-white border border-border p-7 hover:-translate-y-2 hover:shadow-xl hover:shadow-forest/5 transition-all duration-300">
                    <div className="relative h-16 w-16 rounded-full overflow-hidden ring-2 ring-terracotta/40 mb-5">
                      {s.image
                        ? <img src={fileUrl(s.image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        : <span className="flex h-full w-full items-center justify-center bg-brand/10"><Icon className="text-brand" size={24} /></span>}
                    </div>
                    <h3 className="font-display text-2xl text-forest mb-2">{s.title}</h3>
                    <p className="text-sm text-stone leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">
                      En savoir plus <ArrowRight size={15} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Pour qui ?</Overline>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest">Une expertise pour chaque client</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {segments.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div data-testid={`segment-${s.title.toLowerCase()}`}
                className="group h-full rounded-2xl border-2 border-border bg-white p-8 hover:border-brand hover:-translate-y-2 transition-all duration-300">
                <s.icon className="text-terracotta mb-5" size={36} strokeWidth={1.5} />
                <h3 className="font-display text-3xl text-forest mb-3">{s.title}</h3>
                <p className="text-stone leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REALISATIONS PREVIEW */}
      {realisations.length > 0 && (
        <section className="bg-forest py-24 md:py-32" data-testid="realisations-preview">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <Overline>Nos réalisations</Overline>
                <h2 className="mt-4 font-display text-4xl lg:text-5xl text-cream">Des projets qui parlent d'eux-mêmes</h2>
              </div>
              <Link to="/realisations" className="inline-flex items-center gap-2 text-cream font-semibold hover:text-terracotta transition-colors">
                Toutes nos réalisations <ArrowRight size={18} />
              </Link>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {realisations.map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 0.1}>
                  <Link to={`/realisations/${r.slug}`} data-testid={`realisation-${r.slug}`} className="group block relative rounded-2xl overflow-hidden aspect-[4/5]">
                    <img src={fileUrl(r.cover_image) || HERO} alt={r.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                    <div className="absolute bottom-0 p-6">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-terracotta">{r.category}</span>
                      <h3 className="font-display text-2xl text-cream mt-1">{r.title}</h3>
                      {r.location && <p className="text-sm text-cream/60 flex items-center gap-1 mt-1"><MapPin size={13} />{r.location}</p>}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <GoogleReviews />

      {/* CTA / FORM */}
      <section className="py-24 md:py-32 bg-[#F0EFEA]" data-testid="home-contact">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Parlons de votre projet</Overline>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest leading-tight">Demandez votre étude personnalisée gratuite</h2>
            <p className="mt-5 text-stone leading-relaxed">
              Un projet d'aménagement, un entretien régulier ou une étude technique ? Laissez-nous vos coordonnées, un expert vous recontacte sous 24h.
            </p>
            <div className="mt-8 space-y-3 text-sm text-forest">
              <p className="flex items-center gap-3"><MapPin size={18} className="text-brand" /> 665 Avenue du Raumartin, 13730 Saint-Victoret</p>
              <p className="flex items-center gap-3"><Clock size={18} className="text-brand" /> Réponse sous 24h · Devis gratuit</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-white border border-border p-8 shadow-sm">
              <CallbackForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
